/* eslint-disable no-magic-numbers -- surface tokens are named by number */

import { Button } from '../button/Button';
import { Icon } from '../icon';
import styled, { css, keyframes } from 'styled-components';

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(100%);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const slideOut = keyframes`
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(100%);
    }
`;

export const StatusToastContainerWrapper = styled.div`
    position: fixed;
    top: 16px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
    align-items: flex-end;
`;

export const StatusToastItemWrapper = styled.div`
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform, opacity, margin-top;
`;

export const StatusToastItem = styled.div<{ 
	$isExiting: boolean;
}>`
    padding: 16px;
    border-radius: 8px;
    width: 360px;
    // A floating surface: a step above the panel, lifted by the raised shadow and
    // edged by the inset highlight instead of a stroke.
    background-color: ${props => props.theme.surface[3]};
    box-shadow: ${props => props.theme.shadow.raised}, ${props => props.theme.shadow.inset};
    align-items: center;
    pointer-events: auto;
    animation: ${({ $isExiting }) => ($isExiting ? slideOut : slideIn)} 0.3s ease-in-out forwards;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                margin-top 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                margin-bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.3s ease-in-out;
    will-change: transform, margin-top, margin-bottom, opacity;
`;

export const StatusToastWrapperContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 18px;
`;

export const StatusToastWrapperContentText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`;

export const StatusToastTextHeadline = styled.p`
    font-size: 16px;
    font-weight: 600;
    font-family: ${props => props.theme.font};
    line-height: 140%;
    color: ${props => props.theme.text.primary};
    margin: 0;
`;

export const StatusToastTextMessage = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    font-family: ${props => props.theme.font};
    color: ${props => props.theme.text.secondary};
    margin: 0;
`;

export const StatusToastButtonCTA = styled(Button)`
    width: fit-content;
    margin-left: auto;
`;

export const ReplyToastIconWrapper = styled.span`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    font-size: 18px;
`;

export const ReplyToastActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
`;

export const StyledIcon = styled(Icon)<{ $variant: 'success' | 'error' | 'warning' | 'info' | 'close' }>`
    font-size: 24px;
    ${({ $variant }) => $variant === 'success' && css`
        color: ${props => props.theme.text.success};
    `}
    ${({ $variant }) => $variant === 'error' && css`
        color: ${props => props.theme.text.danger};
    `}
    ${({ $variant }) => $variant === 'warning' && css`
        color: ${props => props.theme.text.warning};
    `}
    ${({ $variant }) => $variant === 'info' && css`
        color: ${props => props.theme.border.infoSecondary};
    `}
    ${({ $variant }) => $variant === 'close' && css`
        color: ${props => props.theme.text.tertiary};
    `}
`;

const overlayFadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const overlaySlideIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95) translateY(-20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
`;

const OVERLAY_FADE_IN_DURATION = 0.2;
const OVERLAY_FADE_OUT_DURATION = 0.3;

const overlayFadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

const overlaySlideOut = keyframes`
    from {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
    to {
        opacity: 0;
        transform: scale(0.95) translateY(-20px);
    }
`;

export const StatusToastOverlayBackdrop = styled.div<{ $isExiting: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.slideIn.overlay};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    animation: ${({ $isExiting }) => ($isExiting ? overlayFadeOut : overlayFadeIn)} 
        ${({ $isExiting }) => ($isExiting ? OVERLAY_FADE_OUT_DURATION : OVERLAY_FADE_IN_DURATION)}s 
        ${({ $isExiting }) => ($isExiting ? 'ease-in' : 'ease-out')};
    padding: 20px;
`;

export const StatusToastOverlayContent = styled.div<{ $isExiting: boolean }>`
    position: relative;
    background-color: ${props => props.theme.surface[2]};
    border-radius: 8px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: ${props => props.theme.shadow.raised}, ${props => props.theme.shadow.inset};
    animation: ${({ $isExiting }) => ($isExiting ? overlaySlideOut : overlaySlideIn)} 
        0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 24px;
`;

export const StatusToastOverlayCloseButton = styled(Button).attrs({ variant: 'link', size: 'small' })`
    && {
        position: absolute;
        top: 16px;
        right: 16px;
        padding: 8px;
        color: ${props => props.theme.text.tertiary};
        border-radius: 4px;

        ${Icon.cssSelector} {
            font-size: 20px;
            color: inherit;
        }
    }
`;

export const StatusToastOverlayTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    font-family: ${props => props.theme.font};
    line-height: 140%;
    color: ${props => props.theme.text.primary};
    margin: 0 0 16px 0;
    padding-right: 40px;
`;

export const StatusToastOverlayMainText = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    font-family: ${props => props.theme.font};
    color: ${props => props.theme.text.tertiary};
    margin: 0 0 24px 0;
`;

export const StatusToastOverlayItem = styled.div`
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid ${props => props.theme.border.decorative};
    border-radius: 8px;
    background-color: ${props => props.theme.surface[3]};

    &:last-child {
        margin-bottom: 0;
    }
`;

export const StatusToastOverlayItemTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    font-family: ${props => props.theme.font};
    line-height: 140%;
    color: ${props => props.theme.text.primary};
    margin: 0 0 12px 0;
`;

export const StatusToastOverlayItemList = styled.ul`
    margin: 0;
    padding-left: 20px;
    list-style: none;
`;

export const StatusToastOverlayItemBullet = styled.li`
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    font-family: ${props => props.theme.font};
    color: ${props => props.theme.text.tertiary};
    margin-bottom: 8px;
    position: relative;

    &:before {
        content: "•";
        position: absolute;
        left: -16px;
        color: ${props => props.theme.text.tertiary};
    }

    &:last-child {
        margin-bottom: 0;
    }
`;
